import { Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ValueFormatterFunc } from "ag-grid-community";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useCluster } from "../../contexts/ClusterContext";
import { TrackedProject } from "../../types/projectType";
import { ScenarioMetricsMapping } from "../../types/scenarioMetricsType";
import { ScenarioResults } from "../../types/scenarioResultType";
import { ScenarioMetadata } from "../../types/scenarioType";
import { BackButton } from "../common/BackButton";
import { SectionHeader } from "../common/SectionHeader";
import { ABTag } from "../compare/ABTag";
import {
    CompareMetric,
    CompareMetricsTable
} from "../compare/CompareMetricsTable";
import { ScenarioAssumptionsGrid } from "../Scenario/ScenarioAssumptions";
import { currencyFormatter } from "../tables/agGrid/agGridHelpers";
import css from "./CompareHeader.module.scss";
import {
    averageAllocatedCostPerProject,
    averageAssignedUpgradesPerProject,
    averageConstraintsPerProject,
    averageCostPerMw,
    countUpgrades,
    totalAssignedUpgrades,
    totalConstraints,
    totalProjects
} from "./scenarioMetrics";

const averageFormatter: ValueFormatterFunc = (params) =>
    typeof params.value === "number" ? params.value.toFixed(1) : "-";

// Counts and averages that span every project in the scenario read the full
// constraint and upgrade lists, which the second fetch supplies. Render them
// blank until it lands rather than showing the current project's numbers as
// if they were the scenario's.
const buildScenarioTotals = (
    resultsA: ScenarioResults,
    resultsB: ScenarioResults,
    crossProjectReady: boolean
): CompareMetric[] => [
    {
        key: "total-projects",
        label: "Total projects",
        a: totalProjects(resultsA),
        b: totalProjects(resultsB),
        deltaFormat: "absolute"
    },
    {
        key: "total-upgrades",
        label: "Total assigned upgrades",
        a: crossProjectReady ? totalAssignedUpgrades(resultsA) : null,
        b: crossProjectReady ? totalAssignedUpgrades(resultsB) : null
    },
    {
        key: "total-constraints",
        label: "Total constraints",
        a: crossProjectReady ? totalConstraints(resultsA) : null,
        b: crossProjectReady ? totalConstraints(resultsB) : null
    }
];

const buildScenarioAverages = (
    resultsA: ScenarioResults,
    resultsB: ScenarioResults,
    crossProjectReady: boolean
): CompareMetric[] => [
    {
        key: "avg-cost",
        label: "Avg allocated cost / project",
        formatter: currencyFormatter,
        a: averageAllocatedCostPerProject(resultsA),
        b: averageAllocatedCostPerProject(resultsB)
    },
    {
        key: "avg-cost-per-mw",
        label: "Avg $ / MW",
        formatter: currencyFormatter,
        a: averageCostPerMw(resultsA),
        b: averageCostPerMw(resultsB)
    },
    {
        key: "avg-upgrades",
        label: "Avg assigned upgrades / project",
        formatter: averageFormatter,
        a: crossProjectReady
            ? averageAssignedUpgradesPerProject(resultsA)
            : null,
        b: crossProjectReady
            ? averageAssignedUpgradesPerProject(resultsB)
            : null
    },
    {
        key: "avg-constraints",
        label: "Avg constraints / project",
        formatter: averageFormatter,
        a: crossProjectReady ? averageConstraintsPerProject(resultsA) : null,
        b: crossProjectReady ? averageConstraintsPerProject(resultsB) : null
    }
];

export const CompareHeader: React.FC<{
    project: TrackedProject;
    scenarioA: ScenarioMetadata;
    scenarioB: ScenarioMetadata;
    resultsA: ScenarioResults;
    resultsB: ScenarioResults;
    metricsMapping: ScenarioMetricsMapping;
    isLoadingRemaining: boolean;
    onSwap: () => void;
    onExport: () => void;
}> = ({
    project,
    scenarioA,
    scenarioB,
    resultsA,
    resultsB,
    metricsMapping,
    isLoadingRemaining,
    onSwap,
    onExport
}) => {
    const cluster = useCluster();
    const metricsA = metricsMapping[scenarioA.scenarioId];
    const metricsB = metricsMapping[scenarioB.scenarioId];

    const numUpgradesA = countUpgrades(resultsA, project.projectId);
    const numUpgradesB = countUpgrades(resultsB, project.projectId);

    const crossProjectReady = !isLoadingRemaining;
    const scenarioTotals = buildScenarioTotals(
        resultsA,
        resultsB,
        crossProjectReady
    );
    const scenarioAverages = buildScenarioAverages(
        resultsA,
        resultsB,
        crossProjectReady
    );
    const projectMetrics: CompareMetric[] = [
        {
            key: "sum-allocated-costs",
            label: "Sum of allocated costs",
            formatter: currencyFormatter,
            a: metricsA?.sumAllocatedCost ?? null,
            b: metricsB?.sumAllocatedCost ?? null
        },
        {
            key: "num-upgrades-assigned",
            label: "Num. upgrades assigned",
            a: numUpgradesA,
            b: numUpgradesB,
            deltaFormat: "absolute"
        },
        {
            key: "cost-per-mw",
            label: "$ / MW",
            formatter: currencyFormatter,
            a: metricsA?.costPerMw ?? null,
            b: metricsB?.costPerMw ?? null
        }
    ];

    return (
        <>
            <div className={css["back-button"]}>
                <BackButton
                    text={`Back to Project ${project.projectId}`}
                    to={`/${IN_QUEUE_PATH}/${cluster.slug}/${project.projectId}`}
                />
            </div>
            <SectionHeader
                level="h1"
                title={`Scenario Comparison - ${project.projectId}`}
                actions={
                    <>
                        <Button
                            icon={IconNames.EXPORT}
                            onClick={onExport}
                            text="Export results"
                            size="large"
                        />
                        <Button
                            icon={IconNames.SWAP_HORIZONTAL}
                            onClick={onSwap}
                            intent="primary"
                            text="Swap A / B"
                            size="large"
                        />
                    </>
                }
            />

            <ScenarioAssumptionsGrid
                scenarios={[
                    {
                        scenario: scenarioA,
                        title: (
                            <>
                                <ABTag scenario="a" /> {scenarioA.title}
                            </>
                        )
                    },
                    {
                        scenario: scenarioB,
                        title: (
                            <>
                                <ABTag scenario="b" /> {scenarioB.title}
                            </>
                        )
                    }
                ]}
                showEmptyInputData
                renderInPanel
            />

            <CompareMetricsTable
                sections={[
                    {
                        key: "scenario-totals",
                        label: "Scenario totals",
                        subtitle: "Across all projects in the scenario",
                        metrics: scenarioTotals
                    },
                    {
                        key: "scenario-averages",
                        label: "Scenario averages",
                        subtitle: "Per project, across the scenario",
                        metrics: scenarioAverages
                    },
                    {
                        key: "project-breakdown",
                        label: "Project breakdown",
                        subtitle: project.projectId,
                        metrics: projectMetrics
                    }
                ]}
            />
        </>
    );
};
